import 'moment-timezone'

import { CampaignPhase, CampaignPostDTO, CampaignStatus, PostState, PostStatus, SocialCampaignDTO } from 'models/socialCampaign'

import moment from 'moment'

/**
 * How a post reads on the page (prototype v3 `statusOf`), from the `state` the backend read: SENT is published,
 * MISSED and SKIPPED are not. A SCHEDULED or FROZEN post whose time passed since that read is read from the clock
 * with the backend's rule (`SocialCampaignPost.state`) until the next read: a draft campaign holds its scheduled
 * posts (written by hand) for ever, a paused one since its pause.
 */
export enum PostDisplayStatus {
  DRAFT = 'DRAFT',
  /** A draft the client approved in the editor, still to launch */
  APPROVED = 'APPROVED',
  WAITING = 'WAITING',
  SCHEDULED = 'SCHEDULED',
  /** Scheduled, ahead, while the campaign is paused */
  HELD = 'HELD',
  PUBLISHED = 'PUBLISHED',
  /** Its time came during a pause or after the end, or a launched campaign's draft past its time: never published */
  NOT_PUBLISHED = 'NOT_PUBLISHED',
}

type CampaignClock = Pick<SocialCampaignDTO, 'status' | 'pausedAt' | 'endedAt'>

export function postDisplayStatus(post: CampaignPostDTO, campaign: CampaignClock, now: Date): PostDisplayStatus {
  const ended = campaign.status === CampaignStatus.ENDED

  switch (post.state) {
    case PostState.DRAFT:
      if (ended) return PostDisplayStatus.NOT_PUBLISHED
      // once launched, approving a draft whose time passed skips it (backend `scheduling`, "approved too late")
      if (campaign.status === CampaignStatus.LIVE && Date.parse(post.publishAt) <= now.getTime()) return PostDisplayStatus.NOT_PUBLISHED
      return post.reviewed ? PostDisplayStatus.APPROVED : PostDisplayStatus.DRAFT
    case PostState.WAITING_FOR_CONTENT:
      return ended ? PostDisplayStatus.NOT_PUBLISHED : PostDisplayStatus.WAITING
    case PostState.SENT:
      return PostDisplayStatus.PUBLISHED
    case PostState.MISSED:
    case PostState.SKIPPED:
      return PostDisplayStatus.NOT_PUBLISHED
    // A state this build does not know yet, or none, reads from the clock too: still counted and shown
    case PostState.SCHEDULED:
    case PostState.FROZEN:
    default: {
      const at = Date.parse(post.publishAt)
      const pausedAt = campaign.pausedAt ? Date.parse(campaign.pausedAt) : null
      const endedAt = ended && campaign.endedAt ? Date.parse(campaign.endedAt) : null

      if (at <= now.getTime()) {
        if (campaign.status === CampaignStatus.DRAFT) return PostDisplayStatus.NOT_PUBLISHED
        if (pausedAt !== null && at >= pausedAt) return PostDisplayStatus.NOT_PUBLISHED
        if (endedAt !== null && at > endedAt) return PostDisplayStatus.NOT_PUBLISHED
        return PostDisplayStatus.PUBLISHED
      }
      if (ended) return PostDisplayStatus.NOT_PUBLISHED
      return pausedAt !== null ? PostDisplayStatus.HELD : PostDisplayStatus.SCHEDULED
    }
  }
}

export interface CampaignPostCounts {
  drafts: number
  approved: number
  waiting: number
  scheduled: number
  held: number
  published: number
  notPublished: number
  /** Scheduled posts whose time came during the current pause: never published, though still stored as scheduled */
  missedDuringPause: number
  /** Not out yet and still to go out: drafts, approved, waiting, scheduled and held */
  ahead: number
}

export function countCampaignPosts(campaign: SocialCampaignDTO, now: Date): CampaignPostCounts {
  const counts: CampaignPostCounts = {
    drafts: 0, approved: 0, waiting: 0, scheduled: 0, held: 0, published: 0, notPublished: 0, missedDuringPause: 0, ahead: 0,
  }

  for (const post of campaign.posts) {
    const status = postDisplayStatus(post, campaign, now)
    switch (status) {
      case PostDisplayStatus.DRAFT: counts.drafts++; break
      case PostDisplayStatus.APPROVED: counts.approved++; break
      case PostDisplayStatus.WAITING: counts.waiting++; break
      case PostDisplayStatus.SCHEDULED: counts.scheduled++; break
      case PostDisplayStatus.HELD: counts.held++; break
      case PostDisplayStatus.PUBLISHED: counts.published++; break
      case PostDisplayStatus.NOT_PUBLISHED:
        counts.notPublished++
        if (post.status === PostStatus.SCHEDULED && campaign.pausedAt) counts.missedDuringPause++
        break
    }
  }
  counts.ahead = counts.drafts + counts.approved + counts.waiting + counts.scheduled + counts.held

  return counts
}

/** What the line under the campaign's title says, per phase (prototype v3 `campaignHtml` meta). */
export type CampaignHeaderMeta =
  /** "Starts Wed 14 Oct · 7 posts · 1 reserved for an order" */
  | { kind: 'draft', startDay: string, posts: number, reserved: number }
  /** "Ended · 5 posts published" */
  | { kind: 'ended', published: number }
  /** "3 posts published · 4 posts ahead · 1 waiting for content" */
  | { kind: 'running', published: number, ahead: number, waiting: number }

export function campaignHeaderMeta(campaign: SocialCampaignDTO, now: Date): CampaignHeaderMeta {
  const counts = countCampaignPosts(campaign, now)

  switch (campaign.phase) {
    case CampaignPhase.DRAFT: {
      const reserved = campaign.posts.filter((post) => post.status === PostStatus.WAITING_FOR_CONTENT).length
      const first = campaign.posts.find((post) => post.status !== PostStatus.WAITING_FOR_CONTENT)
      return {
        kind: 'draft',
        startDay: first ? campaignDayOf(first.publishAt, campaign.timezone) : campaign.plan.start,
        posts: campaign.posts.length - reserved,
        reserved,
      }
    }
    case CampaignPhase.ENDED:
      return { kind: 'ended', published: counts.published }
    case CampaignPhase.LIVE:
    case CampaignPhase.PAUSED:
    case CampaignPhase.QUIET:
      return { kind: 'running', published: counts.published, ahead: counts.scheduled + counts.held, waiting: counts.waiting }
  }
}

/** Whether the campaign has gone live and not ended: the Pause switch and the ⋯ menu show. */
export const isCampaignRunning = (phase: CampaignPhase) =>
  phase === CampaignPhase.LIVE || phase === CampaignPhase.PAUSED || phase === CampaignPhase.QUIET

/** The day (YYYY-MM-DD) an instant falls on in the campaign's timezone. */
export const campaignDayOf = (instant: string, timezone: string) => moment(instant).tz(timezone).format('YYYY-MM-DD')

/** English as the prototype writes it (en-GB) for English or a language Intl cannot take ('', 'de_DE', 'cimode'). */
export const dayLocaleOf = (language: string | undefined) => {
  if (!language || language.toLowerCase().startsWith('en')) return 'en-GB'
  try {
    return Intl.DateTimeFormat.supportedLocalesOf(language)[0] ?? 'en-GB'
  } catch {
    return 'en-GB'
  }
}

const dateFormats = new Map<string, Intl.DateTimeFormat>()

/** One `Intl.DateTimeFormat` per locale and options: the schedule's rows re-render on every clock tick. */
export const dateFormatOf = (locale: string, options: Intl.DateTimeFormatOptions) => {
  const key = `${locale} ${JSON.stringify(options)}`
  let format = dateFormats.get(key)
  if (!format) {
    format = new Intl.DateTimeFormat(locale, options)
    dateFormats.set(key, format)
  }
  return format
}

/**
 * A day as the campaign shows it in the UI language: "Wed 14 Oct" in English (prototype `fmtDay`, hence
 * en-GB), each other language's own short form ("Mi., 14. Okt.", "mer. 14 oct."). Runs in renders, so it
 * never throws: an unreadable day shows as it came.
 */
export const formatCampaignDay = (day: string, language: string) => {
  const date = new Date(`${day}T00:00:00Z`)
  if (Number.isNaN(date.getTime())) return day
  return dateFormatOf(dayLocaleOf(language), { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }).format(date)
}
